import React, { useEffect, useState } from 'react';
import { IconButton } from '../ui';
import { isIOS, isStandalone } from '../../lib/pwa';

// PR 4d — Add-to-Home-Screen tutorial card for iOS Safari.
//
// iOS Safari does NOT fire `beforeinstallprompt` like Chrome does, so
// we can't trigger the install programmatically. The best we can do is
// teach the crew lead how: tap the share sheet → "Add to Home Screen".
// Android Chrome handles its own native prompt without needing this
// card, so we hide on non-iOS.
//
// Visibility rules:
//   • Hidden if running in standalone mode (already installed).
//   • Hidden if dismissed by the user this session (state-only, not
//     persisted — like the push opt-in banner, we'd rather nudge again
//     than disappear forever).
//   • Hidden on non-iOS browsers (Chrome handles install via its own
//     UI; Firefox doesn't really do PWA install on Android).

const SHARE_ICON = (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 16V4" />
        <path d="M8 8l4-4 4 4" />
        <rect x="4" y="14" width="16" height="6" rx="2" />
    </svg>
);

const PLUS_BOX_ICON = (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <path d="M12 8v8M8 12h8" />
    </svg>
);

export default function InstallPrompt() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (isStandalone()) return;
        if (!isIOS()) return;
        setVisible(true);
    }, []);

    if (!visible) return null;

    return (
        <div className="p-3 rounded-xl border border-slate-200 bg-white">
            <div className="flex items-start gap-3">
                <div className="text-2xl leading-none" aria-hidden="true">📱</div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900">Install on your home screen</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                        Get to the portal in one tap. In Safari, tap the share button
                        <span className="inline-flex items-center mx-1 align-text-bottom text-slate-700">{SHARE_ICON}</span>
                        then choose
                        <span className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 bg-slate-100 rounded text-slate-800 font-medium">
                            {PLUS_BOX_ICON}
                            Add to Home Screen
                        </span>
                        .
                    </div>
                </div>
                <IconButton size="sm" label="Dismiss install prompt" onClick={() => setVisible(false)} className="-mt-1 -mr-1">
                    ×
                </IconButton>
            </div>
        </div>
    );
}
